import { toast } from '@qodo/design-system';
import { isAxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { ServerError } from '../../types/augmentations/react-query';
import { getErrorToastContent } from './utils';

/**
 * Returns a safe, serialisable error summary — omits Axios request config to
 * avoid leaking Authorization headers in logs.
 * @param error - The caught error value (any shape).
 * @returns An object with `message` and `status` for Axios errors; the error
 * message string for plain `Error` instances; or a stringified fallback otherwise.
 */
export const sanitizeError = (error: unknown) =>
  isAxiosError(error)
    ? { message: error.message, status: error.response?.status }
    : error instanceof Error
      ? error.message
      : String(error);

/**
 * Shows a toast for an API error.
 * - 403 Forbidden: shows the backend-provided message (via `getErrorToastContent`) as a warning.
 *   Only 403 surfaces server messages — other statuses (e.g. 5xx) intentionally use the fallback
 *   to avoid exposing raw server errors to the user.
 * - All other errors: shows `fallbackMessage` as an error toast.
 * @param error - The caught error value (any shape).
 * @param fallbackMessage - Displayed when the error is not a 403 or carries no usable backend message.
 */
export const showApiErrorToast = (error: unknown, fallbackMessage: string) => {
  const serverError = isAxiosError(error) ? (error as ServerError) : undefined;

  if (serverError?.response?.status === StatusCodes.FORBIDDEN) {
    toast.warning(getErrorToastContent(serverError, fallbackMessage));
  } else {
    toast.error(fallbackMessage);
  }
};
